import React, { useCallback, useEffect, useState } from 'react';
import { styled as styled2 } from '@compiled/react';
import Button from '@atlaskit/button';
import { Field as AkField } from '@atlaskit/form';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
// eslint-disable-next-line import/order
import { token } from '@atlaskit/tokens';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import { useCommonPageContext } from '@atlassian/jira-servicedesk-common-page-context';
import type { Intl } from '@atlassian/jira-shared-types/src/general.tsx';
import type { GoalWithId, FormValues } from '../../../../../../../../common/types';
import { NEW } from '../../../../../../../../common/ui/constants.tsx';
import { tableColumnNew } from '../../../../../../../../common/ui/styled';
import {
	getSubGoalValuesById,
	toGoalField,
	getGoalValuesById,
} from '../../../../../../../../common/utils/goal/field';
import { formatDurationOnSave } from '../../../../../../../../common/utils/goal/validators/index.tsx';
import { useSLAHierarchicalGoalsEnabled } from '../../../../../../../../controllers/enable-sla-hierarchical-goals';
import { useValidateJQLService } from '../../../../../../../../services/jql-validation/index.tsx';
import { GoalActions } from './actions';
import { CalendarSelectField } from './calendar';
import { DraggableField } from './draggable';
import { DurationField } from './duration';
import { JqlField } from './jql';
import messages from './messages';
import PrioritiesSelect from './priorities';
import { RemoveGoalButton } from './remove-goal-button';
import { useSubGoalsState } from './state';
import { getNextGoalState } from './utils';

type Props = Intl & {
	index: number;
	goal: GoalWithId;
	getFormValues: () => FormValues;
	onSaveGoal: (goal: GoalWithId) => void;
	onRemoveGoal: () => void;
};

type EditComponentName = 'duration' | 'jql' | 'calendar' | 'priority';

export const EditableGoalView = ({
	intl: { formatMessage },
	onRemoveGoal,
	index,
	onSaveGoal,
	goal,
	getFormValues,
}: Props) => {
	const { baseUrl } = useCommonPageContext();
	const [{ error: jqlValidationError, loading }, { onValidateJql, onCancel: onSaveCancel }] =
		// @ts-expect-error(TS2322 baseUrl, projectId or projectKey can be undefined, if usePageContext() returns null. Please handle undefined value in your code) - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
		useValidateJQLService(baseUrl);
	const [editingState, setEditingState] = useState({
		isEditing: false,
		componentTriggerEdit: undefined,
	});
	const { isEditing, componentTriggerEdit } = editingState;

	const onToggleEditing = useCallback(
		(componentName?: EditComponentName) => {
			setEditingState({
				isEditing: !isEditing,
				// @ts-expect-error - TS2322 - Type 'EditComponentName | undefined' is not assignable to type 'undefined'.
				componentTriggerEdit: componentName,
			});
		},
		[isEditing],
	);
	const onCancel = useCallback(() => {
		// cancel on new goal should remove newly added goal
		// cancel on existing goals should reset goal state
		if (goal.fieldState === NEW) {
			onRemoveGoal();
		} else {
			onSaveGoal(goal);
			onToggleEditing();
		}
		// @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
		onSaveCancel();
	}, [goal, onRemoveGoal, onSaveCancel, onSaveGoal, onToggleEditing]);

	const [subGoals, setSubGoals, onAppendNewSubGoal] = useSubGoalsState(goal.subGoals, goal);
	const onInputChange = useCallback(
		(defaultGoal = false) => {
			const {
				calendarId,
				duration,
				subGoals: newSubGoals = [],
			} = getSubGoalValuesById(getFormValues(), goal.id, subGoals, defaultGoal);
			setSubGoals(newSubGoals);
			onSaveGoal({
				...goal,
				calendarId,
				duration: formatDurationOnSave(duration, formatMessage),
				subGoals: newSubGoals,
				// @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'GoalFieldState | undefined'
				fieldState: getNextGoalState(goal.fieldState),
			});
			onToggleEditing();
		},
		[getFormValues, goal, subGoals, setSubGoals, onSaveGoal, formatMessage, onToggleEditing],
	);
	const [{ isSLAHierarchicalGoalsEnabled }, { updatePrioritiesConsumed }] =
		useSLAHierarchicalGoalsEnabled();

	const onSave = useCallback(async () => {
		// fetch values from form
		if (isSLAHierarchicalGoalsEnabled) {
			const {
				calendarId,
				jqlQuery,
				duration,
				subGoals: newSubGoals = [],
			} = getSubGoalValuesById(getFormValues(), goal.id, subGoals);
			const response = await onValidateJql({ jqlQuery, defaultGoal: goal.defaultGoal });
			if (response) {
				setSubGoals(newSubGoals);
				onSaveGoal({
					...goal,
					calendarId,
					jqlQuery,
					duration: formatDurationOnSave(duration, formatMessage),
					subGoals: newSubGoals,
					// @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'GoalFieldState | undefined'
					fieldState: getNextGoalState(goal.fieldState),
				});
				onToggleEditing();
			}
		} else {
			const { calendarId, jqlQuery, duration } = getGoalValuesById(getFormValues(), goal.id);
			const response = await onValidateJql({ jqlQuery, defaultGoal: goal.defaultGoal });
			if (response) {
				onSaveGoal({
					...goal,
					calendarId,
					jqlQuery,
					duration: formatDurationOnSave(duration, formatMessage),
					// @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'GoalFieldState | undefined'.
					fieldState: getNextGoalState(goal.fieldState),
				});
				onToggleEditing();
			}
		}
	}, [
		goal,
		subGoals,
		isSLAHierarchicalGoalsEnabled,
		getFormValues,
		setSubGoals,
		onValidateJql,
		onSaveGoal,
		formatMessage,
		onToggleEditing,
	]);

	const onRemoveSubGoal = (removedSubGoalIndex: number) => {
		const subGoalsAfterRemoval = subGoals.filter(
			(_, subGoalIndex) => subGoalIndex !== removedSubGoalIndex,
		);
		setSubGoals(subGoalsAfterRemoval);
		onSaveGoal({
			...goal,
			subGoals: subGoalsAfterRemoval,
			// @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'GoalFieldState | undefined'
			fieldState: getNextGoalState(goal.fieldState),
		});
	};

	const onJqlEditRequested = useCallback(() => {
		onToggleEditing('jql');
	}, [onToggleEditing]);

	const onDurationEditRequested = useCallback(() => {
		onToggleEditing('duration');
	}, [onToggleEditing]);

	const onCalendarEditRequested = useCallback(() => {
		onToggleEditing('calendar');
	}, [onToggleEditing]);

	const onPriorityEditRequested = useCallback(() => {
		onToggleEditing('priority');
	}, [onToggleEditing]);

	useEffect(() => {
		const prioritiesSelected = subGoals.map((subGoal) => {
			const [_, value] = subGoal.jqlQuery.split('=');
			const trimmedValue = value?.trim().replace(/^"+|"+$/g, '');
			return trimmedValue;
		});
		updatePrioritiesConsumed(goal.metricId, goal.id, prioritiesSelected);
	}, [subGoals, goal, updatePrioritiesConsumed]);

	return isSLAHierarchicalGoalsEnabled ? (
		<GoalContainer
			data-testid={`servicedesk-sla-settings.ui.metrics.metric.editable.form.goals.goal.fields.goal-container-${goal.id}`}
		>
			{!goal.defaultGoal && (
				<>
					<Row>
						<HandleFieldWrapper invisible={goal.defaultGoal}>
							<DraggableField
								id={goal.id}
								index={index}
								label={formatMessage(messages.dragGoal)}
								isDefaultGoal={goal.defaultGoal}
							/>
						</HandleFieldWrapper>
						<JqlFieldWrapperNew>
							<JqlField
								focusOnEdit
								id={goal.id}
								metricId={goal.metricId}
								onEditRequested={onJqlEditRequested}
								isEditing
								defaultGoal={goal.defaultGoal}
								jqlQuery={goal.jqlQuery}
								jqlValidationError={jqlValidationError}
								validateJQL={onSave}
								areSubGoalsPresent={!!subGoals?.length}
							/>
						</JqlFieldWrapperNew>
						<CalendarFieldWrapperNew />
						<GoalFieldWrapperNew />
						<RemoveGoalWrapper>
							{!goal.defaultGoal && <RemoveGoalButton onRemove={onRemoveGoal} />}
						</RemoveGoalWrapper>
					</Row>
					{!goal.defaultGoal && subGoals.length > 0 && (
						<Row>
							<HandleFieldWrapper />
							<Box xcss={propertyHeaderStyles}>{formatMessage(messages.priorityHeader)}</Box>
						</Row>
					)}
					{subGoals.map((subGoal, subGoalIndex) => {
						const [key, value] = subGoal.jqlQuery.split('=');
						return (
							<TableRow key={subGoal.id}>
								<HandleFieldPlaceholderWithFF />
								<SubGoalContainer>
									<PriorityFieldWrapperNewWithFF>
										{(key === undefined || key.trim() === 'priority') && (
											<PrioritiesSelect
												focusOnEdit={componentTriggerEdit === 'priority'}
												id={subGoal.id}
												goalId={goal.id}
												metricId={goal.metricId}
												onEditRequested={onPriorityEditRequested}
												selectedPriority={value.trim()}
												onChange={() => onInputChange(false)}
												isEditing
											/>
										)}
									</PriorityFieldWrapperNewWithFF>
									<CalendarFieldWrapperNewWithFF>
										<CalendarSelectField
											focusOnEdit={componentTriggerEdit === 'calendar'}
											id={subGoal.id}
											goalId={goal.id}
											calendarId={subGoal.calendarId}
											onEditRequested={onCalendarEditRequested}
											onChange={() => onInputChange(false)}
											isEditing
										/>
									</CalendarFieldWrapperNewWithFF>
									<GoalFieldWrapperNewWithFF>
										<DurationField
											focusOnEdit={componentTriggerEdit === 'duration'}
											id={subGoal.id}
											goalId={goal.id}
											duration={subGoal.duration}
											onEditRequested={onDurationEditRequested}
											onChange={() => onInputChange(false)}
											isEditing
										/>
									</GoalFieldWrapperNewWithFF>
								</SubGoalContainer>
								<RemoveGoalWrapper>
									{!goal.defaultGoal && (
										<RemoveGoalButton onRemove={() => onRemoveSubGoal(subGoalIndex)} />
									)}
								</RemoveGoalWrapper>
							</TableRow>
						);
					})}
					<TableRow>
						<HandleFieldPlaceholderWithFF />
						<SubGoalContainer>
							<JqlPlaceholderWithFF>
								{subGoals?.length > 0 ? (
									<p>{formatMessage(messages.allRemainingPriorities)}</p>
								) : (
									<p>{formatMessage(messages.allMatchingPriorities)}</p>
								)}
							</JqlPlaceholderWithFF>
							<CalendarFieldWrapperNewWithFF>
								<CalendarSelectField
									focusOnEdit={componentTriggerEdit === 'calendar'}
									id={goal.id}
									goalId="goal"
									calendarId={goal.calendarId}
									onEditRequested={onCalendarEditRequested}
									onChange={() => onInputChange(false)}
									isEditing
								/>
							</CalendarFieldWrapperNewWithFF>
							<GoalFieldWrapperNewWithFF>
								<DurationField
									focusOnEdit={componentTriggerEdit === 'duration'}
									id={goal.id}
									goalId="goal"
									duration={goal.duration}
									onEditRequested={onDurationEditRequested}
									onChange={() => onInputChange(false)}
									isEditing
								/>
							</GoalFieldWrapperNewWithFF>
						</SubGoalContainer>
						<RemoveGoalWrapper />
					</TableRow>
					<Row>
						<HandleFieldPlaceholder />
						<AddPriorityButton
							appearance="subtle"
							onClick={onAppendNewSubGoal}
							testId="servicedesk-sla-settings.ui.metrics.metric.editable.form.goals.goal.fields.add-priority-button"
						>
							{formatMessage(messages.addPriority)}
						</AddPriorityButton>
					</Row>
				</>
			)}

			{goal.defaultGoal && (
				<TableRow>
					<td />
					<HandleFieldWrapper invisible={goal.defaultGoal}>
						<DraggableField
							id={goal.id}
							index={index}
							label={formatMessage(messages.dragGoal)}
							isDefaultGoal={goal.defaultGoal}
						/>
					</HandleFieldWrapper>
					<PriorityFieldWrapperNewWithFF>
						<JqlField
							focusOnEdit
							id={goal.id}
							onEditRequested={onJqlEditRequested}
							isEditing
							defaultGoal={goal.defaultGoal}
							jqlQuery={goal.jqlQuery}
							jqlValidationError={jqlValidationError}
							validateJQL={onSave}
						/>
					</PriorityFieldWrapperNewWithFF>
					<CalendarFieldWrapperNewWithFF>
						<CalendarSelectField
							focusOnEdit={componentTriggerEdit === 'calendar'}
							id={goal.id}
							goalId="goal"
							calendarId={goal.calendarId}
							onEditRequested={onCalendarEditRequested}
							isEditing
							onChange={() => onInputChange(true)}
						/>
					</CalendarFieldWrapperNewWithFF>
					<GoalFieldWrapperNewWithFF>
						<DurationField
							focusOnEdit={componentTriggerEdit === 'duration'}
							id={goal.id}
							goalId="goal"
							duration={goal.duration}
							onEditRequested={onDurationEditRequested}
							onChange={() => onInputChange(true)}
							isEditing
						/>
					</GoalFieldWrapperNewWithFF>
					<RemoveGoalWrapper />
				</TableRow>
			)}

			<AkField name={toGoalField(goal.id)} defaultValue={{ ...goal, subGoals }}>
				{() => null}
			</AkField>
		</GoalContainer>
	) : (
		<GoalContainer
			data-testid={`servicedesk-sla-settings.ui.metrics.metric.editable.form.goals.goal.fields.goal-container-${goal.id}`}
		>
			<Row>
				<HandleFieldWrapper invisible={goal.defaultGoal}>
					<DraggableField
						id={goal.id}
						index={index}
						label={formatMessage(messages.dragGoal)}
						isDefaultGoal={goal.defaultGoal}
					/>
				</HandleFieldWrapper>
				<GoalFieldWrapper>
					<DurationField
						focusOnEdit={componentTriggerEdit === 'duration'}
						id={goal.id}
						duration={goal.duration}
						onEditRequested={onDurationEditRequested}
						isEditing={isEditing}
					/>
				</GoalFieldWrapper>
				<CalendarFieldWrapper>
					<CalendarSelectField
						focusOnEdit={componentTriggerEdit === 'calendar'}
						id={goal.id}
						calendarId={goal.calendarId}
						onEditRequested={onCalendarEditRequested}
						isEditing={isEditing}
					/>
				</CalendarFieldWrapper>
				<JqlFieldWrapper>
					<JqlField
						focusOnEdit={componentTriggerEdit === 'jql'}
						id={goal.id}
						onEditRequested={onJqlEditRequested}
						isEditing={isEditing}
						defaultGoal={goal.defaultGoal}
						jqlQuery={goal.jqlQuery}
						jqlValidationError={jqlValidationError}
					/>
				</JqlFieldWrapper>
				<RemoveGoalWrapper>
					{!isEditing && !goal.defaultGoal && <RemoveGoalButton onRemove={onRemoveGoal} />}
				</RemoveGoalWrapper>
			</Row>
			<AkField name={toGoalField(goal.id)} defaultValue={goal}>
				{() => null}
			</AkField>
			{isEditing && (
				<GoalActionContainer>
					<GoalActions
						id={goal.id}
						isDefaultGoal={goal.defaultGoal}
						loading={loading}
						onSaveGoal={onSave}
						onCancel={onCancel}
					/>
				</GoalActionContainer>
			)}
		</GoalContainer>
	);
};

export const EditableGoal = injectIntl(EditableGoalView);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GoalContainer = styled2.div({
	display: 'block',
	borderRadius: '3px',
	boxShadow: token(
		'elevation.shadow.raised',
		'0 1px 1px 0 rgba(9, 30, 66, 0.25), 0 0 1px 0 rgba(9, 30, 66, 0.31)',
	),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	background: token('elevation.surface.raised', colors.N0),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Row = styled2.div({
	display: 'flex',
	flexDirection: 'row',
	paddingBottom: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GoalActionContainer = styled2.div({
	display: 'block',
	padding: `0 ${token('space.050', '4px')} ${token('space.050', '4px')} 0`,
	marginTop: token('space.negative.300', '-24px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const JqlFieldWrapper = styled2(tableColumnNew)({
	flexGrow: 2,
	flexShrink: 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	flexBasis: `${gridSize * 8}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GoalFieldWrapper = styled2(tableColumnNew)({
	flexGrow: 1,
	flexShrink: 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	flexBasis: `${gridSize * 6}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CalendarFieldWrapper = styled2(tableColumnNew)({
	flexGrow: 1,
	flexShrink: 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	flexBasis: `${gridSize * 12}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const JqlFieldWrapperNew = styled2(tableColumnNew)({
	flexGrow: 3,
	flexShrink: 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	flexBasis: `${gridSize * 10}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GoalFieldWrapperNew = styled2(tableColumnNew)({
	flexGrow: 1,
	flexShrink: 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	flexBasis: `${gridSize * 2}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CalendarFieldWrapperNew = styled2(tableColumnNew)({
	flexGrow: 2,
	flexShrink: 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	flexBasis: `${gridSize * 6}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HandleFieldWrapper = styled2(tableColumnNew)({
	paddingTop: token('space.050', '4px'),
	flexGrow: 0,
	flexShrink: 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	flexBasis: `${gridSize * 2}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RemoveGoalWrapper = styled2(tableColumnNew)({
	paddingTop: token('space.200', '16px'),
	flexGrow: 0,
	flexShrink: 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	flexBasis: `${gridSize * 5}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HandleFieldPlaceholder = styled2(tableColumnNew)({
	flexGrow: 0,
	flexShrink: 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	flexBasis: `${gridSize * 4}px`,
});

const propertyHeaderStyles = xcss({
	marginInlineStart: 'space.300',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AddPriorityButton = styled2(Button)({
	marginInlineStart: '4px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	span: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		color: token('color.text.brand', colors.B400),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SubGoalContainer = styled2.div({
	display: 'flex',
	width: '100%',
	padding: token('space.050', '4px'),
	margin: `${token('space.050', '4px')} ${token('space.150', '12px')}`,
	boxShadow: token(
		'elevation.shadow.raised',
		'0 1px 1px rgba(9,30,66,0.25), 0 0 1px 1px rgba(9,30,66,0.13)',
	),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableRow = styled2.tr({
	display: 'flex',
	flexDirection: 'row',
	paddingBottom: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const tableColumnNewWithFF = styled2.td<{ invisible?: boolean }>({
	paddingRight: token('space.100', '8px'),
	textOverflow: 'ellipsis',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	visibility: (props) => (props.invisible ? 'hidden' : 'visible'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PriorityFieldWrapperNewWithFF = styled2(tableColumnNewWithFF)({
	flexGrow: 2,
	flexShrink: 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	flexBasis: `${gridSize * 3}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const JqlPlaceholderWithFF = styled2(PriorityFieldWrapperNewWithFF)({
	margin: 'auto',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	p: {
		padding: token('space.150', '12px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GoalFieldWrapperNewWithFF = styled2(tableColumnNewWithFF)({
	flexGrow: 1,
	flexShrink: 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	flexBasis: `${gridSize * 2}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CalendarFieldWrapperNewWithFF = styled2(tableColumnNewWithFF)({
	flexGrow: 2,
	flexShrink: 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	flexBasis: `${gridSize * 6}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HandleFieldPlaceholderWithFF = styled2(tableColumnNewWithFF)({
	flexGrow: 0,
	flexShrink: 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	flexBasis: `${gridSize * 4}px`,
});
